import { useEffect, useState } from "react";
import axios from "axios";
import { Card, Col, Row, Skeleton } from "antd";
import { Event, EventResponsibility } from "@bindeleddet/core/types";
import ResponsibilityBox from "./ResponsibilityBox";

const Responsibilities = () => {
  const [responsibilities, setResponsibilities] = useState<EventResponsibility[]>([]);
  const [events, setEvents] = useState<Record<number, Event>>({});
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get<EventResponsibility[]>("/events/member-responsibility/get_blmember_event_responsibility/")
      .then(response => {
        setResponsibilities(response.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (responsibilities.length === 0) return;

    const eventIds = responsibilities.map(r => r.event);

    // Fetch events in parallel for each event ID
    Promise.all(
      eventIds.map(eventId =>
        axios
          .get<Event>(`/events/admin-events/${eventId}/`)
          .then(response => ({ id: eventId, event: response.data }))
          .catch(() => null)
      )
    ).then(results => {
      const eventsMap: Record<number, Event> = {};
      results.forEach(result => {
        if (result) {
          eventsMap[result.id] = result.event;
        }
      });
      setEvents(eventsMap);
    });
  }, [responsibilities]);

  // Ghost loader skeleton
  const GhostLoader = () => (
    <Card bodyStyle={{ padding: "0.5em", paddingLeft: "0.8em" }} style={{ marginBottom: 12 }}>
      <Row>
        <Col md={4} sm={6} xs={6}>
          <Skeleton.Image style={{ width: "100%", height: 80 }} />
        </Col>
        <Col md={12} sm={16} xs={18} style={{ paddingLeft: "1em", paddingTop: "0.5em", paddingBottom: "0.5em" }}>
          <Skeleton active paragraph={{ rows: 2 }} title={{ width: "80%" }} />
        </Col>
        <Col md={8} sm={24} xs={24} style={{ textAlign: "center", paddingTop: "7px" }}>
          <Skeleton.Button active size="default" style={{ margin: "0 5px" }} />
          <Skeleton.Button active size="default" style={{ margin: "0 5px" }} />
          <Skeleton.Button active size="default" style={{ margin: "0 5px" }} />
        </Col>
      </Row>
    </Card>
  );

  // Don't show anything while loading responsibilities
  if (loading) {
    return null;
  }

  // Don't show anything if there are no responsibilities
  if (responsibilities.length === 0) {
    return null;
  }

  const filteredResponsibilities = responsibilities.filter(responsibility => {
    const event = events[responsibility.event];
    if (!event) return true; // Keep if event not loaded yet, will show ghost loader

    const eventDate = new Date(event.start);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);

    const daysDifference = Math.ceil((eventDate.getTime() - now.getTime()) / (1000 * 3600 * 24));

    // Filter out events older than 5 days
    if (daysDifference < -5) return false;

    // If MP (responsibility_type === 3), only show if event is today or in the future
    return !(responsibility.responsibility_type === 3 && daysDifference < 0);
  });

  if (filteredResponsibilities.length === 0) {
    return null;
  }

  const handleEventUpdate = (updatedEvent: Event) => {
    setEvents(prevEvents => ({
      ...prevEvents,
      [updatedEvent.id]: updatedEvent,
    }));
  };

  return (
    <>
      {filteredResponsibilities.map((responsibility, index) => {
        const event = events[responsibility.event];
        const isLast = index === filteredResponsibilities.length - 1;

        // Show ghost loader if event is not loaded yet (but we know there's a responsibility)
        if (!event) {
          return <GhostLoader key={responsibility.id} />;
        }

        return (
          <ResponsibilityBox
            key={responsibility.id}
            event={event}
            company={event.company}
            date={event.start}
            responsibility_type={responsibility.responsibility_type}
            isLast={isLast}
            onEventUpdate={handleEventUpdate}
          />
        );
      })}
    </>
  );
};

export default Responsibilities;
